import React, {useState} from 'react';
import moment from 'moment';
import {
  DashboardLayout,
  EmptyState,
  Loader,
  PageLimit,
  Pagination,
  SearchInput,
  SecondaryButton,
  TableLayout,
} from '../../../components';
import {useLocation, useNavigate} from 'react-router-dom';
import {Constants} from '../../../helpers';
import {useQuery} from 'react-query';
import {fetchAuthentications} from '../../../requests/queries/easyAuth';
import {useAllApps} from '../../../hooks/useApps';
import {getStatusTextAndStyles} from '../../../helpers/getStatusTextAndStyles';
import FilterV2 from 'components/FilterV2';
import PageNumber from 'components/PageNumber';

function Authentications() {
  const {state} = useLocation();
  const navigate = useNavigate();
  const {appIds, getAppNameById} = useAllApps();
  const [searchTerm, setSearchTerm] = useState(state?.refId);
  const [filterValues, setFilterValues] = useState({});
  const [showFilter, setShowFilter] = useState(false);

  const appIDS = appIds?.join(',');

  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
  });

  const handleFilterChange = newValues => {
    setFilterValues(newValues);
    setQuery(prev => ({...prev, page: 1}));
  };

  const pageClick = selected => {
    setQuery({
      ...query,
      page: selected,
    });
  };

  const dateParams = filterValues.dateRange
    ? {
        start_date: moment(filterValues.dateRange.start).format('YYYY-MM-DD'),
        end_date: moment(filterValues.dateRange.end).format('YYYY-MM-DD'),
      }
    : {};

  const {data: allEasyAuths, isLoading} = useQuery(
    ['all-auths', {...query, app_ids: appIDS, term: searchTerm, ...dateParams}],
    () =>
      fetchAuthentications({
        ...query,
        app_ids: appIDS,
        term: searchTerm,
        ...dateParams,
      }),
    {
      enabled: appIds?.length > 0,
      retry: 1,
    },
  );

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue flex items-center gap-2">
          Authentications
          {searchTerm && (
            <span className="font-medium text-grey-200 uppercase">
              - {searchTerm}
            </span>
          )}
        </p>
      }
    >
      <div className="flex items-center py-2 flex-wrap gap-3 justify-between mx-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <SearchInput
          onChange={value => {
            setQuery({...query, page: 1});
            setSearchTerm(value);
          }}
          defaultValue={searchTerm}
        />

        <SecondaryButton buttonText="Filter" onClick={toggleFilterVisibility} />
      </div>

      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2 onFilterChange={handleFilterChange} filters={[]} />
      </div>

      <TableLayout negativeMargins className="pt-6">
        <thead className="font-semibold uppercase">
          <tr className="bg-white80">
            <th className="p-5 pl-12 font-medium text-left text-xs text-grey whitespace-nowrap">
              <span className="flex items-center">Name</span>
            </th>
            <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap">
              <span className="flex items-center">Reference id</span>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <span className="flex items-center">App name</span>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <span className="flex items-center">auth id</span>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              <span className="flex items-center">status</span>
            </th>
            <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
              date and time created
            </th>
          </tr>
        </thead>

        <tbody className="text-sm text-body bg-white">
          <tr>
            <td colSpan={8}>
              {isLoading ? (
                <Loader height={45} />
              ) : !isLoading && allEasyAuths?.meta?.total_count === 0 ? (
                <EmptyState
                  noBtn
                  body="No Authentication record found"
                  height={45}
                  heading="No Record found"
                />
              ) : null}
            </td>
          </tr>
        </tbody>

        <tbody className="text-sm bg-white">
          {!isLoading &&
            allEasyAuths &&
            allEasyAuths?.data?.map(auth => (
              <tr
                onClick={() =>
                  navigate(
                    Constants.PAGES_URL.AUTHENTICATIONS_BIO_DATA?.replace(
                      ':slug',
                      auth?.authentication_id,
                    ),
                  )
                }
                key={auth?.authentication_id}
                className="border-[0.5px] cursor-pointer border-[#E4E3EC]"
              >
                <td className="p-4 pl-12 whitespace-nowrap">
                  {`${auth?.first_name || 'N/A'} ${auth?.last_name || ''}`}
                </td>
                <td className="p-4 pl-0 whitespace-nowrap">
                  {auth?.reference_id || '-'}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  {getAppNameById(auth?.app_id) || ''}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  {auth?.authentication_id || ''}
                </td>
                <td className={`p-4 pl-0 text-sm text-body whitespace-nowrap `}>
                  <div
                    className={`uppercase pt-[5px] pb-[3px] px-2 text-xs font-medium   rounded-full w-fit flex items-start gap-[2px] ${
                      getStatusTextAndStyles(auth?.status)?.styles
                    }`}
                  >
                    {getStatusTextAndStyles(auth?.status)?.text || ''}
                  </div>
                </td>
                <td className="p-4 pl-0 whitespace-nowrap">
                  {moment(auth?.datetime).format('Do MMM YYYY hh:mm A')}
                </td>
              </tr>
            ))}
        </tbody>
      </TableLayout>

      {!isLoading && allEasyAuths?.meta?.total_count !== 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
          <PageNumber
            currentPage={+query?.page}
            limit={query?.limit}
            totalCount={allEasyAuths?.meta?.total_count}
          />

          <div className="flex items-center gap-5">
            <PageLimit
              onLimitChange={limit => {
                setQuery(prev => ({...prev, limit, page: 1}));
              }}
              total={allEasyAuths?.meta?.total_count}
              length={allEasyAuths?.data?.length}
              limit={query?.limit}
            />

            <Pagination
              total={Math.ceil(allEasyAuths?.meta?.total_count / query?.limit)}
              current={+query.page}
              onPageChange={activePage => {
                pageClick(activePage);
                const searchParams = new URLSearchParams(
                  document.location.search,
                );
                searchParams.set('page', activePage);
                const newSearchParams = searchParams.toString();
                navigate(`?${newSearchParams}`);
              }}
            />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default Authentications;
